import { useState, useEffect, useCallback, Fragment } from "react";
import API from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ShoppingCart, Copy, ArrowsClockwise, Info, Lightning, Clock } from "@phosphor-icons/react";
import { toast } from "sonner";

// Publisher's Amazon (Shopnomix) page — single feed view with tracker
// URL builder, daily breakdown, and rate card. The pub-facing rate card
// is display-only; actual EPC is computed daily by Shopnomix's reported
// revenue → shave engine. Days without a synced report show "Pending"
// (we don't fabricate numbers).
export default function PubAmazon() {
  const [feed, setFeed] = useState(null);
  const [stats, setStats] = useState(null);
  const [clicks, setClicks] = useState({ data: [], total: 0 });
  const [domains, setDomains] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [customUrl, setCustomUrl] = useState("");
  // Per-day locale drawer — null when collapsed, day-date string when one
  // row is expanded. Only one day open at a time keeps the UI quiet.
  const [expandedDate, setExpandedDate] = useState(null);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [f, s, ck, dm] = await Promise.all([
        API.get("/publisher/shopnomix/feed").catch((e) => ({ error: e })),
        API.get("/publisher/shopnomix/stats").catch(() => null),
        API.get("/publisher/shopnomix/clicks?page_size=50").catch(() => null),
        API.get("/publisher/shopnomix/domains").catch(() => null),
      ]);
      if (f.error) {
        const status = f.error?.response?.status;
        if (status === 404) {
          setError("No Amazon feed assigned to your account yet. Contact admin to get onboarded.");
        } else {
          setError(f.error?.response?.data?.detail || "Failed to load Amazon feed");
        }
      } else {
        setFeed(f.data);
      }
      if (s?.data) setStats(s.data);
      if (ck?.data) setClicks(ck.data);
      if (dm?.data) setDomains(dm.data);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const trackerUrl = feed?.tracker_url || "";
  const trackerUrls = feed?.tracker_urls || [];

  const buildCustomPreview = () => {
    if (!trackerUrl) return "";
    const params = new URLSearchParams();
    if (customUrl) params.set("url", customUrl);
    params.set("cid", "UNIQUE_YOUR_CLICK_ID");
    params.set("source", "homepage");
    // Strip any pre-baked `?url=…` so we start fresh.
    const base = trackerUrl.split("?")[0];
    return `${base}?${params.toString()}`;
  };

  const copy = (text) => {
    navigator.clipboard.writeText(text);
    toast.success("Copied to clipboard");
  };

  if (loading) {
    return (
      <div className="p-8" data-testid="pub-amazon-loading">
        <p className="text-slate-400">Loading Amazon feed…</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-8" data-testid="pub-amazon-empty">
        <div className="border border-amber-200 bg-amber-50 rounded-md p-6 max-w-2xl">
          <div className="flex items-start gap-3">
            <Info size={22} className="text-amber-700 mt-0.5" />
            <div>
              <h3 className="font-heading font-bold text-amber-900">Amazon EPC not enabled</h3>
              <p className="text-sm text-amber-800 mt-1">{error}</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-8" data-testid="pub-amazon">
      <div className="flex items-center justify-between mb-6">
        <div>
          <p className="text-[10px] font-bold tracking-[0.3em] uppercase text-slate-400">Amazon</p>
          <h1 className="font-heading text-2xl font-bold text-slate-900 flex items-center gap-2">
            <ShoppingCart size={26} weight="bold" /> {feed?.brand?.name}
          </h1>
          <p className="text-sm text-slate-500 mt-1">{feed?.brand?.description}</p>
        </div>
        <Button variant="outline" size="sm" onClick={fetchAll}>
          <ArrowsClockwise size={14} className="mr-1.5" /> Refresh
        </Button>
      </div>

      {/* Live snapshot */}
      <div className="grid grid-cols-4 gap-4 mb-6">
        <div className="border border-slate-200 rounded-md p-4">
          <p className="text-[10px] uppercase tracking-[0.2em] font-bold text-slate-400">Clicks (30d)</p>
          <p className="text-2xl font-mono font-bold text-slate-900 mt-1">{stats?.total_clicks ?? 0}</p>
          <p className="text-[10px] text-slate-400 mt-1">live from your tracker</p>
        </div>
        <div className="border border-slate-200 rounded-md p-4">
          <p className="text-[10px] uppercase tracking-[0.2em] font-bold text-slate-400">Confirmed clicks</p>
          <p className="text-2xl font-mono font-bold text-slate-900 mt-1">{stats?.total_feed_clicks ?? 0}</p>
          <p className="text-[10px] text-slate-400 mt-1">{stats?.confirmed_days ?? 0} day(s) confirmed</p>
        </div>
        <div className="border border-slate-200 rounded-md p-4 bg-amber-50/30">
          <p className="text-[10px] uppercase tracking-[0.2em] font-bold text-amber-700">Pending</p>
          <p className="text-2xl font-mono font-bold text-slate-900 mt-1 flex items-center gap-2">
            <Clock size={20} weight="bold" /> {stats?.pending_days ?? 0}
          </p>
          <p className="text-[10px] text-slate-400 mt-1">day(s) awaiting Shopnomix</p>
        </div>
        <div className="border border-slate-200 rounded-md p-4 bg-emerald-50/30">
          <p className="text-[10px] uppercase tracking-[0.2em] font-bold text-emerald-700">Earnings (30d)</p>
          <p className="text-2xl font-mono font-bold text-emerald-700 mt-1">
            ${(stats?.total_earnings ?? 0).toFixed(2)}
          </p>
          <p className="text-[10px] text-slate-500 mt-1">USD · finalized days only</p>
        </div>
      </div>

      {/* Tracker URL builder + rate card */}
      <div className="grid grid-cols-3 gap-6 mb-6">
        <div className="col-span-2 border border-slate-200 rounded-md p-5">
          <div className="flex items-center justify-between mb-3">
            <p className="text-[10px] font-bold tracking-[0.3em] uppercase text-slate-400">
              Your tracker URLs · {trackerUrls.length} locale{trackerUrls.length === 1 ? "" : "s"}
            </p>
            <p className="text-[10px] text-slate-400">
              Need a different storefront? Contact admin to enable it.
            </p>
          </div>
          <p className="text-xs text-slate-500 mb-3">
            Each row routes traffic to the matching Amazon storefront via Shopnomix. Pick the URL that matches the country of your audience and replace
            <code className="bg-slate-100 px-1 mx-1 rounded">UNIQUE_YOUR_CLICK_ID</code> and
            <code className="bg-slate-100 px-1 mx-1 rounded">homepage</code> with your own values per Shopnomix's
            <a href="https://docs.shpnmx.com/redirect-api.html" target="_blank" rel="noopener noreferrer" className="text-[#002FA7] hover:underline ml-1">Redirect API</a>.
          </p>
          <div className="space-y-2" data-testid="pa-tracker-list">
            {trackerUrls.map((row) => (
              <div key={row.root_domain} className="border border-slate-200 rounded-md p-3 bg-slate-50/40" data-testid={`pa-tracker-${row.code || row.root_domain}`}>
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <span className="text-lg">{row.flag}</span>
                    <span className="font-bold text-sm text-slate-900">{row.country}</span>
                    <span className="font-mono text-[10px] text-slate-500">· {row.root_domain}</span>
                    <span className="font-mono text-[10px] text-slate-400">· {row.currency}</span>
                  </div>
                  <Button size="sm" variant="outline" onClick={() => copy(row.tracker_url_full)} data-testid={`pa-copy-${row.code || row.root_domain}`}>
                    <Copy size={12} className="mr-1" /> Copy
                  </Button>
                </div>
                <Input value={row.tracker_url_full} readOnly className="font-mono text-[11px] bg-white" />
              </div>
            ))}
            {trackerUrls.length === 0 && (
              <div className="text-xs text-slate-500 py-4 text-center border border-dashed border-slate-200 rounded-md">
                No locales enabled yet. Contact admin to enable Amazon storefronts for your account.
              </div>
            )}
          </div>

          <details className="mt-4 group" data-testid="pa-custom-url-block">
            <summary className="text-[11px] font-bold text-slate-500 cursor-pointer hover:text-slate-900 select-none">
              Need a custom landing URL? (advanced)
            </summary>
            <div className="pt-3 space-y-2">
              <Label className="text-[10px] uppercase tracking-wider font-bold text-slate-500">Custom Amazon URL</Label>
              <Input value={customUrl} onChange={(e) => setCustomUrl(e.target.value)} placeholder="https://amazon.com/dp/B0XXXXXXXX" className="font-mono text-xs" data-testid="pa-deeplink" />
              <p className="text-[10px] text-slate-400">
                Pass any deeplink within an enabled Amazon storefront (e.g. a specific product page on amazon.com or amazon.de). The host must match one of your enabled locales above — otherwise Shopnomix will reject the click.
              </p>
              {customUrl && (
                <div className="flex gap-2 mt-2">
                  <Input value={buildCustomPreview()} readOnly className="font-mono text-[11px] bg-slate-50" data-testid="pa-tracker-url" />
                  <Button variant="outline" size="sm" onClick={() => copy(buildCustomPreview())} data-testid="pa-copy-btn">
                    <Copy size={14} />
                  </Button>
                </div>
              )}
            </div>
          </details>
        </div>

        <div className="border border-amber-200 bg-amber-50/40 rounded-md p-5">
          <div className="flex items-center gap-2 mb-3">
            <Lightning size={16} className="text-amber-700" weight="fill" />
            <p className="text-[10px] font-bold tracking-[0.3em] uppercase text-amber-700">Rate card</p>
          </div>
          <p className="text-xs text-slate-600 mb-3">
            Your daily EPC is finalized after Shopnomix reports back (typically T+24h). Indicative rates by conversion quality:
          </p>
          <div className="space-y-2 font-mono text-xs">
            {feed?.rate_card?.map((t, i) => (
              <div key={i} className="flex justify-between items-center py-1 border-b border-amber-200/40">
                <span className="text-slate-700">{t.slab}</span>
                <span className="font-bold text-slate-900">{t.pub_epc}</span>
              </div>
            ))}
          </div>
          <p className="text-[10px] text-slate-500 mt-3">
            Earnings top out at $0.27 per click. <span className="font-bold text-slate-700">80/20 split</span> applies to all tiers.
          </p>
        </div>
      </div>

      {/* Daily breakdown + clicks log */}
      <Tabs defaultValue="days">
        <TabsList className="mb-4">
          <TabsTrigger value="days" data-testid="pa-tab-days">Daily breakdown ({stats?.days?.length || 0})</TabsTrigger>
          <TabsTrigger value="regions" data-testid="pa-tab-regions">By region ({domains?.domains?.length || 0})</TabsTrigger>
          <TabsTrigger value="clicks" data-testid="pa-tab-clicks">Clicks log ({clicks.total || 0})</TabsTrigger>
        </TabsList>

        <TabsContent value="days">
          <div className="border border-slate-200 rounded-md overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-slate-50">
                <tr className="border-b border-slate-200">
                  <th className="text-left px-4 py-2.5 text-xs uppercase tracking-wider text-slate-500 font-bold">Date</th>
                  <th className="text-right px-4 py-2.5 text-xs uppercase tracking-wider text-slate-500 font-bold">Your clicks</th>
                  <th className="text-right px-4 py-2.5 text-xs uppercase tracking-wider text-slate-500 font-bold">Confirmed</th>
                  <th className="text-right px-4 py-2.5 text-xs uppercase tracking-wider text-slate-500 font-bold">EPC</th>
                  <th className="text-right px-4 py-2.5 text-xs uppercase tracking-wider text-emerald-700 font-bold bg-emerald-50/40">Earnings</th>
                  <th className="text-center px-4 py-2.5 text-xs uppercase tracking-wider text-slate-500 font-bold">Status</th>
                </tr>
              </thead>
              <tbody>
                {/* Latest day first — reverse-chronological so the most
                    recent (and most relevant) traffic is what the user
                    sees on opening the tab, no scrolling needed. */}
                {[...(stats?.days || [])].sort((a, b) => (b.date || "").localeCompare(a.date || "")).map((d) => {
                  const dateKey = String(d.date).slice(0, 10);
                  const isOpen = expandedDate === dateKey;
                  const hasDomainSplit = (d.by_domain || []).length > 0;
                  return (
                    <Fragment key={dateKey}>
                      <tr
                        className={`border-b border-slate-100 hover:bg-slate-50 ${hasDomainSplit ? "cursor-pointer" : ""}`}
                        onClick={() => hasDomainSplit && setExpandedDate(isOpen ? null : dateKey)}
                        data-testid={`daily-row-${dateKey}`}
                      >
                        <td className="px-4 py-2.5 font-mono text-xs">
                          {hasDomainSplit && (
                            <span className="inline-block w-3 mr-1 text-slate-400 text-[10px]">
                              {isOpen ? "▾" : "▸"}
                            </span>
                          )}
                          {dateKey}
                        </td>
                        <td className="px-4 py-2.5 text-right font-mono text-sm">{d.clicks}</td>
                        <td className="px-4 py-2.5 text-right font-mono text-sm">
                          {d.feed_clicks ?? <span className="text-slate-400">—</span>}
                        </td>
                        <td className="px-4 py-2.5 text-right font-mono text-sm">
                          {d.pub_epc !== null && d.pub_epc !== undefined ? `$${d.pub_epc.toFixed(2)}` : <span className="text-slate-400">—</span>}
                        </td>
                        <td className="px-4 py-2.5 text-right font-mono text-sm font-bold bg-emerald-50/30">
                          {d.earnings !== null && d.earnings !== undefined ? `$${d.earnings.toFixed(2)}` : <span className="text-slate-400 font-normal">—</span>}
                        </td>
                        <td className="px-4 py-2.5 text-center">
                          {d.status === "pending" ? (
                            <span
                              className="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-amber-50 text-amber-700 text-[10px] font-medium cursor-help"
                              title="Shopnomix typically reports back T+24h. Earnings will populate once confirmed."
                            >
                              <Clock size={10} weight="bold" /> Pending
                            </span>
                          ) : (
                            <span className="px-2 py-0.5 rounded bg-emerald-50 text-emerald-700 text-[10px] font-medium">
                              Confirmed
                            </span>
                          )}
                        </td>
                      </tr>
                      {isOpen && hasDomainSplit && (
                        <tr
                          className="bg-slate-50/60 border-b border-slate-200"
                          data-testid={`daily-drawer-${dateKey}`}
                        >
                          <td colSpan={6} className="px-6 py-3">
                            <div className="text-[10px] font-semibold uppercase tracking-wider text-slate-500 mb-2">
                              Locale split — {dateKey}
                            </div>
                            <div className="grid grid-cols-[1fr_auto_auto_auto] gap-x-6 gap-y-1 text-xs">
                              <div className="font-semibold text-slate-500">Storefront</div>
                              <div className="font-semibold text-slate-500 text-right">Clicks</div>
                              <div className="font-semibold text-slate-500 text-right">Revenue</div>
                              <div className="font-semibold text-slate-500 text-right">Share</div>
                              {d.by_domain.map((dom) => {
                                const share = d.clicks ? (dom.clicks / d.clicks) * 100 : 0;
                                return (
                                  <Fragment key={dom.root_domain}>
                                    <div className="font-mono text-slate-700">{dom.root_domain}</div>
                                    <div className="font-mono text-right">{dom.clicks}</div>
                                    <div className="font-mono text-right">${Number(dom.revenue || 0).toFixed(2)}</div>
                                    <div className="font-mono text-right text-slate-500">{share.toFixed(1)}%</div>
                                  </Fragment>
                                );
                              })}
                            </div>
                          </td>
                        </tr>
                      )}
                    </Fragment>
                  );
                })}
                {(!stats?.days || stats.days.length === 0) && (
                  <tr><td colSpan={6} className="px-4 py-6 text-center text-sm text-slate-400">No traffic in last 30 days. Copy your tracker URL above and start sending clicks.</td></tr>
                )}
              </tbody>
            </table>
          </div>
          <p className="text-[10px] text-slate-400 mt-2">
            <Clock size={10} className="inline mr-1" weight="bold" />
            Pending days will update automatically every hour once Shopnomix confirms the day's traffic. EPC and earnings are final after confirmation.
          </p>
        </TabsContent>

        <TabsContent value="regions">
          <div className="border border-slate-200 rounded-md overflow-hidden" data-testid="pa-regions-panel">
            <div className="grid grid-cols-[1fr_auto_auto_auto_auto] gap-0 bg-slate-50 border-b border-slate-200 px-4 py-2.5 text-[10px] font-bold tracking-[0.2em] uppercase text-slate-500">
              <div>Amazon locale</div>
              <div className="text-right pr-6">Share</div>
              <div className="text-right pr-6">Clicks</div>
              <div className="text-right pr-6">Revenue</div>
              <div className="text-right">Active days</div>
            </div>
            {(domains?.domains || []).map((d) => (
              <div key={d.root_domain} className="grid grid-cols-[1fr_auto_auto_auto_auto] gap-0 px-4 py-3 border-b border-slate-100 hover:bg-slate-50" data-testid={`pa-region-${d.root_domain}`}>
                <div className="flex items-center gap-2">
                  <span className="font-medium text-slate-900 font-mono">{d.root_domain}</span>
                </div>
                <div className="text-right pr-6 font-mono text-sm">
                  <div className="inline-flex items-center gap-2">
                    <div className="w-20 h-1.5 bg-slate-100 rounded-full overflow-hidden">
                      <div
                        className="h-full bg-[#002FA7]"
                        style={{ width: `${Math.min(100, d.share_pct)}%` }}
                      />
                    </div>
                    <span className="w-12 text-right">{d.share_pct}%</span>
                  </div>
                </div>
                <div className="text-right pr-6 font-mono text-sm">{d.clicks}</div>
                <div className="text-right pr-6 font-mono text-sm">${Number(d.revenue).toFixed(2)}</div>
                <div className="text-right font-mono text-sm text-slate-500">{d.days}</div>
              </div>
            ))}
            {(!domains?.domains || domains.domains.length === 0) && (
              <div className="px-4 py-6 text-center text-sm text-slate-400">
                No locale data yet. Rows appear here after Shopnomix reports back (every hour).
              </div>
            )}
            {domains?.domains?.length > 0 && (
              <div className="bg-slate-50 border-t border-slate-200 px-4 py-2.5 text-xs text-slate-600 flex items-center justify-between">
                <span>
                  Total: <span className="font-mono font-bold">{domains.total_clicks}</span> clicks ·{" "}
                  <span className="font-mono font-bold">${Number(domains.total_revenue).toFixed(2)}</span>
                </span>
                <span className="text-[10px] text-slate-400">
                  Should match "Daily breakdown" totals above — if not, tell admin.
                </span>
              </div>
            )}
          </div>
          <p className="text-[10px] text-slate-400 mt-2">
            Split of your traffic across Amazon's international storefronts. Use this to spot unusual locale shifts (possible fraud) or confirm your targeting is going where you expect.
          </p>
        </TabsContent>

        <TabsContent value="clicks">
          <div className="border border-slate-200 rounded-md overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-slate-50">
                <tr className="border-b border-slate-200">
                  <th className="text-left px-4 py-2.5 text-xs uppercase tracking-wider text-slate-500 font-bold">Time</th>
                  <th className="text-left px-4 py-2.5 text-xs uppercase tracking-wider text-slate-500 font-bold">RID</th>
                  <th className="text-left px-4 py-2.5 text-xs uppercase tracking-wider text-slate-500 font-bold">Deeplink</th>
                </tr>
              </thead>
              <tbody>
                {clicks.data?.map((c) => (
                  <tr key={c.rid} className="border-b border-slate-100 hover:bg-slate-50">
                    <td className="px-4 py-2.5 text-xs font-mono">{c.click_date}</td>
                    <td className="px-4 py-2.5 font-mono text-[11px] text-slate-600">{c.rid}</td>
                    <td className="px-4 py-2.5 text-xs text-slate-500 truncate max-w-xs" title={c.deeplink_url}>
                      {c.deeplink_url && c.deeplink_url.replace("https://", "")}
                    </td>
                  </tr>
                ))}
                {(!clicks.data || clicks.data.length === 0) && (
                  <tr><td colSpan={3} className="px-4 py-6 text-center text-sm text-slate-400">No clicks yet.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
